// Discounts tab: percent rules that are always on, in a
// date range, on days of the week, every year in a month or a yearly range;
// presets menu; enable switch and status chip per rule.
import React, { useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { ChevronDownIcon, PlusIcon, TagIcon } from "@heroicons/react/24/outline"
import { Select, DateInput } from "../../../AdminShared/ui"
import { Switch, Chip, Alert, AdornedInput, DeleteIconButton } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const DAYS = [
  { label: "Sun", value: 0 }, { label: "Mon", value: 1 }, { label: "Tue", value: 2 }, { label: "Wed", value: 3 }, { label: "Thu", value: 4 }, { label: "Fri", value: 5 }, { label: "Sat", value: 6 },
]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((label, i) => ({ label, value: i + 1 }))
const DAYS_IN_MONTH = Array.from({ length: 31 }, (_, i) => ({ label: String(i + 1), value: i + 1 }))

const PRESETS = [
  { label: "Every Friday", schedule: { type: "days_of_week", days: [5] } },
  { label: "Every Weekend", schedule: { type: "days_of_week", days: [0, 6] } },
  { label: "Christmas Holidays", schedule: { type: "yearly_range", startMonth: 12, startDay: 24, endMonth: 1, endDay: 2 } },
  { label: "Easter", schedule: { type: "yearly_range", startMonth: 3, startDay: 28, endMonth: 4, endDay: 6 } },
  { label: "Summer", schedule: { type: "yearly_range", startMonth: 6, startDay: 21, endMonth: 9, endDay: 22 } },
  { label: "Winter", schedule: { type: "yearly_range", startMonth: 12, startDay: 1, endMonth: 2, endDay: 28 } },
  { label: "Black Friday", schedule: { type: "yearly_range", startMonth: 11, startDay: 25, endMonth: 11, endDay: 30 } },
  { label: "New Year's", schedule: { type: "yearly_range", startMonth: 12, startDay: 31, endMonth: 1, endDay: 3 } },
  { label: "Valentine's Day", schedule: { type: "yearly_range", startMonth: 2, startDay: 12, endMonth: 2, endDay: 15 } },
  { label: "Halloween", schedule: { type: "yearly_range", startMonth: 10, startDay: 28, endMonth: 11, endDay: 1 } },
  { label: "Every December", schedule: { type: "yearly_month", month: 12 } },
  { label: "Back to School", schedule: { type: "yearly_range", startMonth: 8, startDay: 15, endMonth: 9, endDay: 15 } },
  { label: "Spring Sale", schedule: { type: "yearly_range", startMonth: 3, startDay: 20, endMonth: 4, endDay: 20 } },
  { label: "Every Monday", schedule: { type: "days_of_week", days: [1] } },
  { label: "Every Sunday", schedule: { type: "days_of_week", days: [0] } },
]

const WHEN_OPTIONS = [
  { value: "always", label: "Always (indefinite)" },
  { value: "date_range", label: "Date range" },
  { value: "days_of_week", label: "Days of the week" },
  { value: "yearly_month", label: "Every year — specific month" },
  { value: "yearly_range", label: "Every year — date range" },
]

const generateId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
const getWhenType = (d) => (d.schedule ? d.schedule.type : d.startDate !== undefined || d.endDate !== undefined ? "date_range" : "always")

function getStatus(discount) {
  if (discount.enabled === false) return { label: "Disabled", color: "default" }
  const now = new Date()
  if (!discount.startDate && !discount.endDate && !discount.schedule) return { label: "Always Active", color: "success" }
  if (discount.schedule) return { label: "Recurring", color: "info" }
  const start = discount.startDate ? new Date(discount.startDate) : null
  const end = discount.endDate ? new Date(discount.endDate + "T23:59:59") : null
  if (start && now < start) return { label: "Scheduled", color: "warning" }
  if (end && now > end) return { label: "Expired", color: "default" }
  return { label: "Active", color: "success" }
}

const makeBaseDiscount = (extra = {}) => ({ id: generateId(), amount: 10, type: "percent", createdAt: new Date().toISOString(), ...extra })


function MonthDaySelect({ label, monthValue, dayValue, onMonthChange, onDayChange }) {
  const t = useProductEditorTexts()
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-admin_text_secondary min-w-[30px]">{label}</span>
      <Select value={monthValue || 1} onChange={(v) => onMonthChange(Number(v))} options={MONTHS.map((o) => ({ ...o, label: t(o.label) }))} className="w-[90px]" searchEnabled={false} />
      <Select value={dayValue || 1} onChange={(v) => onDayChange(Number(v))} options={DAYS_IN_MONTH} className="w-[76px]" searchEnabled={false} />
    </div>
  )
}

function DiscountRow({ discount, onUpdate, onRemove }) {
  const t = useProductEditorTexts()
  const whenType = getWhenType(discount)
  const status = getStatus(discount)
  const isEnabled = discount.enabled !== false
  const patch = (changes) => onUpdate(discount.id, { ...discount, ...changes })
  const updateSchedule = (schedule) => patch({ schedule })

  const changeWhenType = (newType) => {
    const base = { id: discount.id, amount: discount.amount, type: discount.type, createdAt: discount.createdAt }
    if (discount.enabled !== undefined) base.enabled = discount.enabled
    if (newType === "always") return onUpdate(discount.id, base)
    if (newType === "date_range") return onUpdate(discount.id, { ...base, startDate: "", endDate: "" })
    const defaults = {
      days_of_week: { type: "days_of_week", days: [5] },
      yearly_month: { type: "yearly_month", month: 12 },
      yearly_range: { type: "yearly_range", startMonth: 12, startDay: 24, endMonth: 12, endDay: 31 },
    }
    onUpdate(discount.id, { ...base, schedule: defaults[newType] })
  }

  return (
    <div className={`p-3 border rounded-md bg-admin_paper transition-opacity ${isEnabled ? "border-admin_bordered" : "border-admin_bordered/50 opacity-60"}`} data-testid="discount-row">
      <div className="flex flex-wrap items-center gap-3">
        <TagIcon className="w-5 h-5 text-admin_text_secondary" />
        <AdornedInput type="number" adornment="%" value={discount.amount} onChange={(v) => patch({ amount: Math.max(1, Math.min(99, parseInt(v) || 1)) })} min={1} max={99} className="w-[120px]" inputClassName="text-right" />
        <Select value={whenType} onChange={changeWhenType} options={WHEN_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))} className="min-w-[220px]" searchEnabled={false} />
        <div className="ml-auto flex items-center gap-2">
          <Chip color={status.color}>{t(status.label)}</Chip>
          <Switch checked={isEnabled} onChange={(v) => patch({ enabled: v })} title={isEnabled ? t`Disable discount` : t`Enable discount`} />
        </div>
        <DeleteIconButton onClick={() => onRemove(discount.id)} />
      </div>
      {whenType === "date_range" && (
        <div className="flex flex-wrap gap-4 mt-3">
          <label className="flex flex-col gap-1 text-xs text-admin_text_secondary">{t`From`}<DateInput value={discount.startDate || ""} onChange={(v) => patch({ startDate: v })} className="w-44" /></label>
          <label className="flex flex-col gap-1 text-xs text-admin_text_secondary">{t`To`}<DateInput value={discount.endDate || ""} onChange={(v) => patch({ endDate: v })} className="w-44" /></label>
        </div>
      )}
      {whenType === "days_of_week" && (
        <div className="flex flex-wrap gap-2 mt-3">
          {DAYS.map((d) => {
            const selected = (discount.schedule.days || []).includes(d.value)
            return (
              <button key={d.value} type="button" onClick={() => updateSchedule({ ...discount.schedule, days: selected ? discount.schedule.days.filter((x) => x !== d.value) : [...(discount.schedule.days || []), d.value].sort((a, b) => a - b) })} className={`h-7 px-3 rounded-full text-xs border ${selected ? "bg-admin_primary border-admin_primary text-white" : "border-admin_bordered text-admin_text"}`}>
                {t(d.label)}
              </button>
            )
          })}
        </div>
      )}
      {whenType === "yearly_month" && (
        <div className="mt-3">
          <Select value={discount.schedule.month || 1} onChange={(v) => updateSchedule({ ...discount.schedule, month: Number(v) })} options={MONTHS.map((o) => ({ ...o, label: t(o.label) }))} className="w-[140px]" searchEnabled={false} />
        </div>
      )}
      {whenType === "yearly_range" && (
        <div className="flex flex-col gap-2 mt-3">
          <MonthDaySelect label={t`From`} monthValue={discount.schedule.startMonth} dayValue={discount.schedule.startDay} onMonthChange={(v) => updateSchedule({ ...discount.schedule, startMonth: v })} onDayChange={(v) => updateSchedule({ ...discount.schedule, startDay: v })} />
          <MonthDaySelect label={t`To`} monthValue={discount.schedule.endMonth} dayValue={discount.schedule.endDay} onMonthChange={(v) => updateSchedule({ ...discount.schedule, endMonth: v })} onDayChange={(v) => updateSchedule({ ...discount.schedule, endDay: v })} />
        </div>
      )}
    </div>
  )
}

export default function DiscountsTab({ editor }) {
  const t = useProductEditorTexts()
  const { read, set } = editor
  // every row needs its own id to be changed or removed alone: an older one (or the importer's,
  // before it gave one) has none, and is given a stable one here — kept once the product is saved
  const discounts = (read("discounts") || []).map((d, index) => (d.id ? d : { ...d, id: d.source === "import" ? "import" : `d${index}-${new Date(d.createdAt || 0).getTime()}` }))
  const onChange = (list) => set("discounts", list)
  const [presetsOpen, setPresetsOpen] = useState(false)
  const menu = useRef(null)

  useEffect(() => {
    const onDown = (e) => menu.current && !menu.current.contains(e.target) && setPresetsOpen(false)
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [])

  const addDiscount = (schedule = null) => onChange([...discounts, makeBaseDiscount(schedule ? { schedule } : {})])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Typography component="h4" variant="admin_h4" className="my-0 inline-flex items-center gap-1.5">{t`Discounts`}<HelpHint helpKey="vendor.product.setup#discounts" /></Typography>
        <div className="flex gap-2">
          <div ref={menu} className="relative">
            <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={() => setPresetsOpen((o) => !o)}>{t`Presets`} <ChevronDownIcon className="w-4 h-4" /></Button>
            {presetsOpen && (
              <ul className="absolute right-0 z-20 mt-1 w-[220px] max-h-72 overflow-y-auto bg-admin_paper border border-admin_bordered rounded-md shadow-lg py-1" role="menu">
                {PRESETS.map((p) => (
                  <li key={p.label} role="menuitem" onClick={() => { setPresetsOpen(false); addDiscount(p.schedule) }} className="px-3 py-2 text-sm cursor-pointer hover:bg-admin_neutral_1">{t(p.label)}</li>
                ))}
              </ul>
            )}
          </div>
          <Button variant="admin_primary" className="h-[32px] text-xs px-3" onClick={() => addDiscount()} data-testid="add-discount"><PlusIcon className="w-4 h-4" /> {t`Add Discount`}</Button>
        </div>
      </div>
      <p className="text-sm text-admin_text_secondary my-0">{t`Discounts apply to both real-money price and coin price. When multiple discounts are active simultaneously, the one created most recently wins.`}</p>
      {discounts.length === 0 && <Alert severity="info">{t`No discounts configured. Add one manually or pick a preset.`}</Alert>}
      <div className="flex flex-col gap-3">
        {discounts.map((d) => (
          <DiscountRow key={d.id} discount={d} onUpdate={(id, replacement) => onChange(discounts.map((x) => (x.id === id ? replacement : x)))} onRemove={(id) => onChange(discounts.filter((x) => x.id !== id))} />
        ))}
      </div>
      {discounts.length > 1 && <span className="text-xs text-admin_text_secondary">{t("%s discounts — created last wins when multiple are active at the same time.", discounts.length)}</span>}
    </div>
  )
}
